// --- START: Translated portfolio data hook ---
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { PortfolioProject, PortfolioImage } from './portfolio';

/**
 * Hook that returns portfolio projects with translations applied
 */
export function usePortfolioProjects(): PortfolioProject[] {
  const { t } = useTranslation('marketing');
  
  return useMemo(() => {
    const projectSlugs = [
      'elephant-decor-cork',
      'bag-closer-pourer',
      'snowflake-decor',
      'big-jar-vase',
      'bottle-holder-hand',
      'skull-glasses-stand',
      'small-jar-ridged'
    ];
    
    return projectSlugs.map((slug) => {
      const base = `portfolio.projects.${slug}`;
      const imageKeys = getImageKeysForProject(slug);
      
      const images: PortfolioImage[] = imageKeys.map((key) => {
        const imagePath = `/images/portfolio/${slug}/${key}.jpg`;
        const altKey = `${base}.images.${key}.alt`;
        const captionKey = `${base}.images.${key}.caption`;
        
        return {
          src: imagePath,
          alt: t(altKey),
          caption: t(captionKey, { defaultValue: '' }) || undefined
        };
      });
      
      return {
        slug,
        title: t(`${base}.title`),
        summary: t(`${base}.summary`),
        category: getCategoryForProject(slug),
        tags: getTagsForProject(slug),
        images,
        coverIndex: 0,
      };
    });
  }, [t]);
}

// Helper to get image keys for each project
function getImageKeysForProject(slug: string): string[] {
  const imageMap: Record<string, string[]> = {
    'elephant-decor-cork': ['cover', '01', '02', '03'],
    'bag-closer-pourer': ['cover', '01'],
    'snowflake-decor': ['cover'],
    'big-jar-vase': ['cover', '01', '02'],
    'bottle-holder-hand': ['cover'],
    'skull-glasses-stand': ['cover'],
    'small-jar-ridged': ['cover'],
  };
  return imageMap[slug] || ['cover'];
}

// Helper to get category for each project
function getCategoryForProject(slug: string): "Decorative" | "Functional" {
  const functionalProjects = ['bag-closer-pourer', 'bottle-holder-hand', 'skull-glasses-stand'];
  return functionalProjects.includes(slug) ? 'Functional' : 'Decorative';
}

// Helper to get tags for each project
function getTagsForProject(slug: string): string[] {
  const tagMap: Record<string, string[]> = {
    'elephant-decor-cork': ['Home', 'Statue'],
    'bag-closer-pourer': ['Home'],
    'snowflake-decor': ['Home'],
    'big-jar-vase': ['Home', 'Vase'],
    'bottle-holder-hand': ['Home', 'Display'],
    'skull-glasses-stand': ['Home', 'Display'],
    'small-jar-ridged': ['Home', 'Vase'],
  };
  return tagMap[slug] || ['Home'];
}
// --- END: Translated portfolio data hook ---
